import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Drag-to-reorder and swipe-to-remove, sharing one surface.
 *
 * POINTER EVENTS, NOT HTML5 DRAG-AND-DROP.
 * The brief originally said native HTML5 drag for pointer devices. That API
 * cannot produce the physics we want — no lift, no gap opening ahead of the
 * row, no control of the drag image on mobile — and it does not fire on touch
 * at all. Pointer Events cover mouse, touch and pen with one code path and give
 * us the frame-by-frame position the effect needs.
 *
 * AXIS LOCK: the two gestures are told apart by which direction the finger
 * commits to first, decided once and never revisited mid-gesture.
 */

/**
 * How far a finger must travel before the gesture commits to an axis.
 *
 * TUNING KNOB — this is the one value here that can only be judged with a
 * thumb, so it is deliberately a single constant. Below ~5px is finger tremor;
 * above ~20px the gesture feels sluggish to start. Change this line alone if it
 * reads twitchy or slow on a real device.
 */
const AXIS_LOCK_PX = 10;
/** A press held this long starts a drag with no movement at all. */
const LONG_PRESS_MS = 250;
/** How far a row must travel toward the start edge before a release removes it. */
const SWIPE_COMMIT_PX = 96;

/**
 * `step` is the distance between consecutive row TOPS, so it includes the
 * form's --gap-row; using the row height alone would open a gap short by
 * exactly the gap on any spacious skin.
 *
 * IT IS MEASURED ONCE, AT POINTERDOWN, AND CARRIED FROM THERE.
 *
 * It used to be measured inside the state updater, from
 * `stepOf(event.currentTarget)` — and that is what turned the screen white on a
 * real phone. React nulls `currentTarget` the moment the synchronous handler
 * returns, because it is only meaningful during dispatch, while a `setState`
 * updater runs later during reconciliation. So the first committed drag move
 * read `null.getBoundingClientRect()`, React unmounted the whole tree, and the
 * user got a blank page with no way back but a reload.
 *
 * Carrying it also removes a forced layout read from every pointermove, which
 * is the difference between one reflow per gesture and one per frame.
 */
type Gesture =
  | { kind: "idle" }
  | { kind: "pending"; code: string; x: number; y: number; step: number }
  | { kind: "drag"; code: string; y: number; dy: number; step: number }
  | { kind: "swipe"; code: string; x: number; progress: number };

export interface RowGestureState {
  readonly draggingCode: string | null;
  readonly dy: number;
  /** Index the dragged row would land on if released now. */
  readonly overIndex: number | null;
  readonly swipingCode: string | null;
  /** Distance travelled toward the inline-start edge, never negative. */
  readonly swipeProgress: number;
  readonly willRemove: boolean;
  /**
   * How far a neighbouring row must move to open the gap, in pixels.
   * Rows between the dragged row's origin and its destination slide one step
   * toward the origin; everything else stays put.
   */
  readonly shiftFor: (index: number) => number;
}

interface Options {
  readonly codes: readonly string[];
  readonly rtl: boolean;
  readonly onReorder: (from: number, to: number) => void;
  readonly onRemove: (code: string) => void;
  /*
   * A press that committed to no axis and never became a long press.
   *
   * The tap belongs HERE rather than to an overlay button, because the row-wide
   * tap target and the row-wide gesture surface are the same pixels, and the
   * gesture wins that fight by construction: pointer capture retargets the
   * eventual `click` to the capturing element, so any button underneath is
   * silently dead to a finger while still firing from element.click(). Layering
   * z-indexes around that only moves the problem. One owner for the pixels.
   */
  readonly onTap: (code: string) => void;
}

/**
 * Distance between this row's top and the next one's — the row height PLUS the
 * form's --gap-row. Measured rather than read from a token because the gap is a
 * form token and this hook must not know which form is active. Falls back to
 * the row's own height for the last row in the list.
 */
function stepOf(row: HTMLElement): number {
  const here = row.getBoundingClientRect();
  const next = row.nextElementSibling?.getBoundingClientRect();
  return next ? Math.max(1, next.top - here.top) : Math.max(1, here.height);
}

export function useRowGestures({ codes, rtl, onReorder, onRemove, onTap }: Options) {
  const [gesture, setGesture] = useState<Gesture>({ kind: "idle" });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  const reset = useCallback(() => {
    cancelTimer();
    setGesture({ kind: "idle" });
  }, []);

  const indexOf = (code: string) => codes.indexOf(code);

  /*
   * STOPS THE PAGE SCROLLING UNDER A COMMITTED GESTURE.
   *
   * `touch-action` alone cannot do this. The browser reads it when the finger
   * first lands, so flipping it to `none` on [data-dragging] arrives after the
   * decision has already been made — which is why the drag fought the scroll on
   * a real phone while the CSS looked correct. The only thing that cancels an
   * in-flight touch scroll is preventDefault() from a NON-PASSIVE touchmove
   * listener, and React's synthetic touchmove is passive, so this is attached
   * natively.
   *
   * Scoped to a committed gesture and torn down by the effect's own cleanup, so
   * scrolling returns on release, on pointercancel, on a mid-gesture unmount,
   * and on a row disappearing under the finger. There is no path that leaves
   * the page locked, because nothing here has to remember to unlock it.
   */
  useEffect(() => {
    if (gesture.kind !== "drag" && gesture.kind !== "swipe") return;
    const block = (event: TouchEvent) => event.preventDefault();
    document.addEventListener("touchmove", block, { passive: false });
    return () => document.removeEventListener("touchmove", block);
  }, [gesture.kind]);

  /** Where the dragged row would land, derived from distance in whole rows. */
  const overIndex = (() => {
    if (gesture.kind !== "drag") return null;
    const from = indexOf(gesture.code);
    if (from === -1) return null;
    const shift = Math.round(gesture.dy / Math.max(1, gesture.step));
    return Math.max(0, Math.min(codes.length - 1, from + shift));
  })();

  /**
   * The gap. Every row the dragged one has passed slides one step back toward
   * the vacated position, which opens a space exactly where the row will land.
   *
   * Applied as a transform, NOT as a transition — so it still opens on a skin
   * whose motion budget is zero. A zero-duration transition animates nothing,
   * but the transform is applied either way; only the easing disappears.
   */
  const shiftFor = (index: number): number => {
    if (gesture.kind !== "drag" || overIndex === null) return 0;
    const from = indexOf(gesture.code);
    if (from === -1 || index === from) return 0;
    if (overIndex > from && index > from && index <= overIndex) return -gesture.step;
    if (overIndex < from && index >= overIndex && index < from) return gesture.step;
    return 0;
  };

  const onPointerDown = useCallback(
    (code: string) => (event: React.PointerEvent<HTMLElement>) => {
      // Ignore secondary buttons; a right-click is not a drag.
      if (event.button !== 0 && event.pointerType === "mouse") return;

      /*
       * A press that begins on a discrete control is that control's, not the
       * row's.
       *
       * Without this the row captured the pointer for EVERY pointerdown, and
       * capture retargets the eventual `click` to the capturing element — so
       * the three-dot button, the caveat marker and the base row's chevron were
       * all dead to a real finger while still firing perfectly from
       * element.click(). Every test in the suite used the latter, so nothing
       * caught it; a real Playwright click did, immediately.
       *
       * Opt-out is an explicit marker rather than "any <button>" on purpose:
       * .row__swap IS a button and must NOT opt out, because it covers the row
       * and a drag has to be able to start on top of it.
       */
      if ((event.target as Element | null)?.closest('[data-gesture="ignore"]')) return;
      /*
       * Pointer capture keeps move events coming once the finger leaves the
       * row, which a vertical drag does immediately. It is an enhancement
       * rather than a requirement — without it the gesture still tracks while
       * the pointer is over the row — so a environment that lacks it degrades
       * instead of throwing. jsdom implements the method but throws from it,
       * and swallowing that here is better than muting errors suite-wide.
       */
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* no capture available; the gesture still works over the row */
      }
      /*
       * Everything the gesture will ever need is read off the event HERE, while
       * the event is still live. Nothing below this line may touch `event`.
       */
      const step = stepOf(event.currentTarget);
      const x = event.clientX;
      const y = event.clientY;

      setGesture({ kind: "pending", code, x, y, step });
      cancelTimer();
      // A held press starts a drag without waiting for movement — the touch
      // affordance, and the reason long-press belongs to rows and not to the
      // appearance preview.
      timer.current = setTimeout(() => {
        setGesture((g) =>
          g.kind === "pending" && g.code === code
            ? { kind: "drag", code, y, dy: 0, step }
            : g,
        );
      }, LONG_PRESS_MS);
    },
    [],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      /*
       * READ THE EVENT BEFORE THE UPDATER, ALWAYS.
       *
       * The updater below runs during reconciliation, not during dispatch. By
       * then React has reset the event's `currentTarget` to null — reading it
       * there is what crashed the app to a white screen. Coordinates are plain
       * numbers and survive, but hoisting them too keeps the rule simple enough
       * to hold: nothing inside the updater mentions `event`.
       */
      const px = event.clientX;
      const py = event.clientY;

      setGesture((g) => {
        if (g.kind === "idle") return g;

        if (g.kind === "pending") {
          const dx = px - g.x;
          const dy = py - g.y;
          if (Math.hypot(dx, dy) < AXIS_LOCK_PX) return g;
          cancelTimer();
          // Locked once, on the first committed movement, and never revisited:
          // a diagonal gesture resolves to whichever axis it favoured rather
          // than doing both badly.
          if (Math.abs(dy) > Math.abs(dx)) {
            return { kind: "drag", code: g.code, y: g.y, dy, step: g.step };
          }
          return { kind: "swipe", code: g.code, x: g.x, progress: 0 };
        }

        if (g.kind === "drag") return { ...g, dy: py - g.y };

        // Toward the inline-start edge, whichever side that is. Never negative:
        // dragging the other way does nothing rather than sliding the row out
        // of its own row.
        const raw = px - g.x;
        return { ...g, progress: Math.max(0, rtl ? raw : -raw) };
      });
    },
    [rtl],
  );

  const onPointerUp = useCallback(() => {
    setGesture((g) => {
      cancelTimer();
      if (g.kind === "drag") {
        const from = indexOf(g.code);
        const shift = Math.round(g.dy / Math.max(1, g.step));
        const to = Math.max(0, Math.min(codes.length - 1, from + shift));
        if (from !== -1 && to !== from) onReorder(from, to);
      } else if (g.kind === "swipe" && g.progress >= SWIPE_COMMIT_PX) {
        onRemove(g.code);
      } else if (g.kind === "pending") {
        /*
         * Still pending at release means the finger never travelled far enough
         * to lock an axis and the long-press timer never fired: a plain tap.
         * A drag that returns to where it started is NOT a tap — it is already
         * kind "drag" by then, and dropping a row where you found it should do
         * nothing.
         */
        onTap(g.code);
      }
      return { kind: "idle" };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes, onReorder, onRemove, onTap]);

  /*
   * A rate refresh re-renders every row mid-gesture. Identity is held as a
   * CODE rather than an index so the row survives that, and a code that leaves
   * the list — removed in another tab, dropped from the snapshot — cancels the
   * gesture instead of reordering something that is no longer there.
   */
  if (gesture.kind !== "idle" && indexOf(gesture.code) === -1) {
    queueMicrotask(reset);
  }

  const state: RowGestureState = {
    draggingCode: gesture.kind === "drag" ? gesture.code : null,
    dy: gesture.kind === "drag" ? gesture.dy : 0,
    overIndex,
    swipingCode: gesture.kind === "swipe" ? gesture.code : null,
    swipeProgress: gesture.kind === "swipe" ? gesture.progress : 0,
    willRemove: gesture.kind === "swipe" && gesture.progress >= SWIPE_COMMIT_PX,
    shiftFor,
  };

  const bind = (code: string) => ({
    onPointerDown: onPointerDown(code),
    onPointerMove,
    onPointerUp,
    onPointerCancel: reset,
  });

  return { state, bind, cancel: reset };
}

/** Moves an item, returning a new array. Shared by drag and keyboard reorder. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...items];
  next.splice(to, 0, moved);
  return next;
}
